import type {
  CampaignDetailDto,
  CampaignStateDto,
  CycleNumberDto,
  CycleStateDto,
} from '@/api/campaign-client';
import { isoToParisInput, parisInputToIso } from '@/lib/paris-time';

// =============================================================================
// calendar-model.ts — Logique PURE du calendrier du détail campagne (dates de campagne + cycles).
// =============================================================================
// GATE_HUMAN 2026-09-16 (John), règles appliquées dans l'UI UNIQUEMENT (réponse 5a : aucune modification
// d'API, de domaine, de contrats ni de Prisma) :
//   - « la date de la campagne conditionne le début du cycle 1 et la fin du cycle 2. La fin du cycle 1
//     conditionne le début du cycle 2 » : ce sont des valeurs par DÉFAUT MODIFIABLES (réponse 1), jamais
//     figées. Une cible reçoit la propagation tant qu'elle est éditable ET qu'elle n'a pas été modifiée à
//     la main pendant la session.
//   - sans Cycle 2, la fin de campagne conditionne la fin du Cycle 1 (réponse 2) ;
//   - un cycle (y compris la prolongation d'un cycle Ouverte) qui se termine après la fin de campagne
//     repousse la fin de campagne (réponse 3) ;
//   - campagne ouverte (état ≠ Brouillon/Planifiee) : seule la FIN de campagne reste modifiable (réponse 4).
// Les valeurs manipulées sont des chaînes « YYYY-MM-DDTHH:mm » en heure de Paris (réponse 5b).
//
// Autorité inchangée = API (422 si début ≥ fin, ouverture ≥ fermeture, chevauchement C1/C2 ; cycle Ouverte :
// ouverture identique et fermeture strictement repoussée ; Cloturee/Verrouillee : 409). Les alertes calculées
// ici sont NON bloquantes : l'API reste juge.

/** Les six champs du calendrier. */
export type CalendarField =
  | 'campaignStart'
  | 'campaignEnd'
  | 'c1Open'
  | 'c1Close'
  | 'c2Open'
  | 'c2Close';

export const CALENDAR_FIELDS: readonly CalendarField[] = [
  'campaignStart',
  'campaignEnd',
  'c1Open',
  'c1Close',
  'c2Open',
  'c2Close',
];

/** Valeurs « YYYY-MM-DDTHH:mm » (heure de Paris) ; chaîne vide = non renseigné. */
export type CalendarValues = Readonly<Record<CalendarField, string>>;

/** Contexte serveur : état de la campagne et état de chaque cycle (null = cycle absent). */
export interface CalendarContext {
  readonly campaignState: CampaignStateDto;
  readonly cycle1: CycleStateDto | null;
  readonly cycle2: CycleStateDto | null;
}

/** Brouillon de saisie : surcharges des valeurs serveur, champs modifiés à la main, ajout du Cycle 2. */
export interface CalendarDraft {
  readonly edits: Readonly<Partial<Record<CalendarField, string>>>;
  readonly manual: ReadonlySet<CalendarField>;
  readonly cycle2Added: boolean;
}

export const EMPTY_DRAFT: CalendarDraft = {
  edits: {},
  manual: new Set(),
  cycle2Added: false,
};

/** Ligne du calendrier (bloc) : sert à l'affichage des erreurs et au séquencement de l'enregistrement. */
export type CalendarLine = 'campaign' | CycleNumberDto;

// -----------------------------------------------------------------------------
// Lecture du serveur
// -----------------------------------------------------------------------------

function cycleOf(
  campaign: Pick<CampaignDetailDto, 'cycles'>,
  n: CycleNumberDto,
): CampaignDetailDto['cycles'][number] | undefined {
  return campaign.cycles.find((c) => c.cycleNumber === n);
}

/** ISO d'origine de chaque champ (renvoyé tel quel s'il n'est pas modifié, cf. cycle Ouverte). */
export function isoFromCampaign(
  campaign: Pick<CampaignDetailDto, 'startDate' | 'endDate' | 'cycles'>,
): Readonly<Record<CalendarField, string | null>> {
  const c1 = cycleOf(campaign, 1);
  const c2 = cycleOf(campaign, 2);
  return {
    campaignStart: campaign.startDate,
    campaignEnd: campaign.endDate,
    c1Open: c1?.openAt ?? null,
    c1Close: c1?.closeAt ?? null,
    c2Open: c2?.openAt ?? null,
    c2Close: c2?.closeAt ?? null,
  };
}

/** Valeurs serveur en heure de Paris. */
export function valuesFromCampaign(
  campaign: Pick<CampaignDetailDto, 'startDate' | 'endDate' | 'cycles'>,
): CalendarValues {
  const iso = isoFromCampaign(campaign);
  const out = {} as Record<CalendarField, string>;
  for (const f of CALENDAR_FIELDS) {
    out[f] = isoToParisInput(iso[f]);
  }
  return out;
}

export function contextFromCampaign(
  campaign: Pick<CampaignDetailDto, 'state' | 'cycles'>,
): CalendarContext {
  return {
    campaignState: campaign.state,
    cycle1: cycleOf(campaign, 1)?.state ?? null,
    cycle2: cycleOf(campaign, 2)?.state ?? null,
  };
}

// -----------------------------------------------------------------------------
// Éditabilité
// -----------------------------------------------------------------------------

/** Campagne « ouverte » au sens de la règle 4 : tout état autre que Brouillon / Planifiee. */
export function isCampaignOpened(state: CampaignStateDto): boolean {
  return state !== 'Brouillon' && state !== 'Planifiee';
}

/** Le Cycle 2 est-il présent (persisté ou ajouté dans la session) ? */
export function hasCycle2(ctx: CalendarContext, draft: CalendarDraft): boolean {
  return ctx.cycle2 !== null || draft.cycle2Added;
}

function cycleState(ctx: CalendarContext, n: CycleNumberDto): CycleStateDto | null {
  return n === 1 ? ctx.cycle1 : ctx.cycle2;
}

/**
 * Un champ est-il éditable ?
 *   - début de campagne : tant que la campagne n'est pas ouverte (règle 4) ;
 *   - fin de campagne : toujours (l'API reste juge) ;
 *   - ouverture de cycle : cycle absent (à planifier) ou Pendante ;
 *   - fermeture de cycle : cycle absent, Pendante, ou Ouverte (prolongation) ;
 *   - Cycle 2 absent et non ajouté : rien à éditer.
 */
export function isFieldEditable(
  field: CalendarField,
  ctx: CalendarContext,
  draft: CalendarDraft,
): boolean {
  switch (field) {
    case 'campaignStart':
      return !isCampaignOpened(ctx.campaignState);
    case 'campaignEnd':
      return true;
    case 'c1Open':
    case 'c1Close':
    case 'c2Open':
    case 'c2Close': {
      const n: CycleNumberDto = field.startsWith('c1') ? 1 : 2;
      if (n === 2 && !hasCycle2(ctx, draft)) {
        return false;
      }
      const state = cycleState(ctx, n);
      if (state === null || state === 'Pendante') {
        return true;
      }
      return state === 'Ouverte' && field.endsWith('Close');
    }
  }
}

// -----------------------------------------------------------------------------
// Saisie et propagation des défauts
// -----------------------------------------------------------------------------

/** Valeurs effectives : surcharges du brouillon sinon valeurs serveur (Cycle 2 retiré s'il est absent). */
export function effectiveValues(
  initial: CalendarValues,
  ctx: CalendarContext,
  draft: CalendarDraft,
): CalendarValues {
  const out = { ...initial, ...draft.edits } as Record<CalendarField, string>;
  if (!hasCycle2(ctx, draft)) {
    out.c2Open = '';
    out.c2Close = '';
  }
  return out;
}

/** Comparaison chronologique de deux saisies Paris ; `null` si l'une est vide ou invalide. */
export function compareInputs(a: string, b: string): number | null {
  const ia = parisInputToIso(a);
  const ib = parisInputToIso(b);
  if (ia === null || ib === null) {
    return null;
  }
  return new Date(ia).getTime() - new Date(ib).getTime();
}

function withEdit(
  edits: Partial<Record<CalendarField, string>>,
  field: CalendarField,
  value: string,
): Partial<Record<CalendarField, string>> {
  return { ...edits, [field]: value };
}

/** Pose une valeur par défaut sur `target` si elle est éditable et non modifiée à la main. */
function propagate(
  edits: Partial<Record<CalendarField, string>>,
  target: CalendarField,
  value: string,
  ctx: CalendarContext,
  draft: CalendarDraft,
): Partial<Record<CalendarField, string>> {
  if (!isFieldEditable(target, ctx, draft) || draft.manual.has(target)) {
    return edits;
  }
  return withEdit(edits, target, value);
}

/**
 * Saisie MANUELLE d'un champ : enregistre la valeur, marque le champ « modifié à la main », puis applique
 * les défauts :
 *   - début de campagne ⇒ ouverture du Cycle 1 ;
 *   - fin de campagne ⇒ fermeture du Cycle 2 s'il est présent, sinon du Cycle 1 ;
 *   - fermeture du Cycle 1 ⇒ ouverture du Cycle 2 (s'il est présent) ;
 *   - fermeture d'un cycle postérieure à la fin de campagne ⇒ la fin de campagne suit (règle 3).
 * Un champ non éditable est ignoré (aucune modification du brouillon).
 */
export function applyManualEdit(
  initial: CalendarValues,
  ctx: CalendarContext,
  draft: CalendarDraft,
  field: CalendarField,
  value: string,
): CalendarDraft {
  if (!isFieldEditable(field, ctx, draft)) {
    return draft;
  }
  const manual = new Set(draft.manual);
  manual.add(field);
  const next: CalendarDraft = { ...draft, manual };
  let edits = withEdit(draft.edits, field, value);

  switch (field) {
    case 'campaignStart':
      edits = propagate(edits, 'c1Open', value, ctx, next);
      break;
    case 'campaignEnd':
      edits = propagate(
        edits,
        hasCycle2(ctx, draft) ? 'c2Close' : 'c1Close',
        value,
        ctx,
        next,
      );
      break;
    case 'c1Close':
      if (hasCycle2(ctx, draft)) {
        edits = propagate(edits, 'c2Open', value, ctx, next);
      }
      edits = followCampaignEnd(initial, ctx, next, edits, value);
      break;
    case 'c2Close':
      edits = followCampaignEnd(initial, ctx, next, edits, value);
      break;
    case 'c1Open':
    case 'c2Open':
      break;
  }
  return { ...next, edits };
}

/** Règle 3 : une fin de cycle postérieure à la fin de campagne repousse la fin de campagne. */
function followCampaignEnd(
  initial: CalendarValues,
  ctx: CalendarContext,
  draft: CalendarDraft,
  edits: Partial<Record<CalendarField, string>>,
  cycleClose: string,
): Partial<Record<CalendarField, string>> {
  if (parisInputToIso(cycleClose) === null) {
    return edits;
  }
  const currentEnd = edits.campaignEnd ?? initial.campaignEnd;
  const cmp = compareInputs(cycleClose, currentEnd);
  if (currentEnd.length > 0 && (cmp === null || cmp <= 0)) {
    return edits;
  }
  if (!isFieldEditable('campaignEnd', ctx, draft)) {
    return edits;
  }
  return withEdit(edits, 'campaignEnd', cycleClose);
}

/**
 * Ajout du Cycle 2 dans la session : ouverture par défaut = fermeture du Cycle 1, fermeture par défaut =
 * fin de campagne (valeurs modifiables).
 */
export function addCycle2(
  initial: CalendarValues,
  ctx: CalendarContext,
  draft: CalendarDraft,
): CalendarDraft {
  if (hasCycle2(ctx, draft)) {
    return draft;
  }
  const values = effectiveValues(initial, ctx, draft);
  const manual = new Set(draft.manual);
  manual.delete('c2Open');
  manual.delete('c2Close');
  return {
    cycle2Added: true,
    manual,
    edits: { ...draft.edits, c2Open: values.c1Close, c2Close: values.campaignEnd },
  };
}

/** Annule l'ajout (non enregistré) du Cycle 2. */
export function cancelCycle2(draft: CalendarDraft): CalendarDraft {
  const edits = { ...draft.edits };
  delete edits.c2Open;
  delete edits.c2Close;
  const manual = new Set(draft.manual);
  manual.delete('c2Open');
  manual.delete('c2Close');
  return { edits, manual, cycle2Added: false };
}

// -----------------------------------------------------------------------------
// Alertes (non bloquantes)
// -----------------------------------------------------------------------------

export const ALERT_AFTER_CAMPAIGN_END = 'Après la fin de campagne';
export const ALERT_BEFORE_CAMPAIGN_START = 'Avant le début de campagne';
export const ALERT_BEFORE_CYCLE1_END = 'Avant la fin du cycle 1';

/**
 * Alertes par champ :
 *   - fermeture d'un cycle postérieure à la fin de campagne ⇒ « Après la fin de campagne » ;
 *   - ouverture du Cycle 1 antérieure au début de campagne ⇒ « Avant le début de campagne » ;
 *   - ouverture du Cycle 2 antérieure à la fermeture du Cycle 1 ⇒ « Avant la fin du cycle 1 ».
 */
export function calendarAlerts(
  values: CalendarValues,
): Readonly<Partial<Record<CalendarField, string>>> {
  const out: Partial<Record<CalendarField, string>> = {};
  const after = (a: string, b: string): boolean => {
    const cmp = compareInputs(a, b);
    return cmp !== null && cmp > 0;
  };
  if (after(values.c1Close, values.campaignEnd)) {
    out.c1Close = ALERT_AFTER_CAMPAIGN_END;
  }
  if (after(values.c2Close, values.campaignEnd)) {
    out.c2Close = ALERT_AFTER_CAMPAIGN_END;
  }
  if (after(values.campaignStart, values.c1Open)) {
    out.c1Open = ALERT_BEFORE_CAMPAIGN_START;
  }
  if (after(values.c1Close, values.c2Open)) {
    out.c2Open = ALERT_BEFORE_CYCLE1_END;
  }
  return out;
}

// -----------------------------------------------------------------------------
// Modifications et plan d'enregistrement
// -----------------------------------------------------------------------------

/** Un champ diffère-t-il de la valeur serveur ? */
export function isFieldDirty(
  initial: CalendarValues,
  ctx: CalendarContext,
  draft: CalendarDraft,
  field: CalendarField,
): boolean {
  return effectiveValues(initial, ctx, draft)[field] !== initial[field];
}

const LINE_FIELDS: Readonly<Record<CalendarLine, readonly CalendarField[]>> = {
  campaign: ['campaignStart', 'campaignEnd'],
  1: ['c1Open', 'c1Close'],
  2: ['c2Open', 'c2Close'],
};

export function lineFields(line: CalendarLine): readonly CalendarField[] {
  return LINE_FIELDS[line];
}

/** Lignes modifiées (un Cycle 2 ajouté compte comme modifié). */
export function dirtyLines(
  initial: CalendarValues,
  ctx: CalendarContext,
  draft: CalendarDraft,
): readonly CalendarLine[] {
  const lines: CalendarLine[] = [];
  for (const line of ['campaign', 1, 2] as const) {
    const dirty = LINE_FIELDS[line].some((f) =>
      isFieldDirty(initial, ctx, draft, f),
    );
    if (dirty || (line === 2 && ctx.cycle2 === null && draft.cycle2Added)) {
      lines.push(line);
    }
  }
  return lines;
}

export type CalendarSaveStep =
  | {
      readonly kind: 'dates';
      readonly line: 'campaign';
      readonly startDate: string | null;
      readonly endDate: string | null;
    }
  | {
      readonly kind: 'cycle';
      readonly line: CycleNumberDto;
      readonly cycleNumber: CycleNumberDto;
      readonly openAt: string;
      readonly closeAt: string;
    }
  | {
      readonly kind: 'incomplete';
      readonly line: CycleNumberDto;
      readonly message: string;
    };

/**
 * Plan d'enregistrement : UNIQUEMENT les lignes modifiées. Ordre : dates de campagne, puis les cycles dans
 * l'ordre qui évite un chevauchement transitoire C1/C2 refusé par l'API — décalage vers le FUTUR
 * (fermeture du Cycle 1 repoussée) : Cycle 2 puis Cycle 1 ; vers le PASSÉ : Cycle 1 puis Cycle 2.
 * Un champ inchangé est renvoyé avec son ISO d'origine (l'API exige une ouverture IDENTIQUE pour un cycle
 * Ouverte). Un cycle incomplet (ouverture ou fermeture vide) produit une étape `incomplete` sans appel.
 */
export function buildSavePlan(
  initial: CalendarValues,
  originalIso: Readonly<Record<CalendarField, string | null>>,
  ctx: CalendarContext,
  draft: CalendarDraft,
): readonly CalendarSaveStep[] {
  const values = effectiveValues(initial, ctx, draft);
  const lines = dirtyLines(initial, ctx, draft);
  const isoOf = (field: CalendarField): string | null =>
    values[field] === initial[field]
      ? originalIso[field]
      : parisInputToIso(values[field]);

  const steps: CalendarSaveStep[] = [];
  if (lines.includes('campaign')) {
    steps.push({
      kind: 'dates',
      line: 'campaign',
      startDate: isoOf('campaignStart'),
      endDate: isoOf('campaignEnd'),
    });
  }

  const cycleStep = (n: CycleNumberDto): CalendarSaveStep => {
    const [openField, closeField] = LINE_FIELDS[n] as readonly [CalendarField, CalendarField];
    const openAt = isoOf(openField);
    const closeAt = isoOf(closeField);
    if (openAt === null || closeAt === null) {
      return {
        kind: 'incomplete',
        line: n,
        message: `Renseignez l'ouverture et la fermeture du Cycle ${String(n)}.`,
      };
    }
    return { kind: 'cycle', line: n, cycleNumber: n, openAt, closeAt };
  };

  const c1Dirty = lines.includes(1);
  const c2Dirty = lines.includes(2);
  if (c1Dirty && c2Dirty) {
    const shift = compareInputs(values.c1Close, initial.c1Close);
    const towardsFuture = ctx.cycle1 !== null && shift !== null && shift > 0;
    const order: readonly CycleNumberDto[] = towardsFuture ? [2, 1] : [1, 2];
    for (const n of order) {
      steps.push(cycleStep(n));
    }
  } else if (c1Dirty) {
    steps.push(cycleStep(1));
  } else if (c2Dirty) {
    steps.push(cycleStep(2));
  }
  return steps;
}

/**
 * Resynchronisation après relecture serveur : retire les surcharges devenues égales aux valeurs serveur
 * (lignes enregistrées) et l'ajout du Cycle 2 dès qu'il est persisté. Les surcharges encore différentes
 * (ligne en erreur, saisie en cours) et les drapeaux « modifié à la main » sont conservés.
 */
export function pruneSynced(
  initial: CalendarValues,
  ctx: CalendarContext,
  draft: CalendarDraft,
): CalendarDraft {
  const edits: Partial<Record<CalendarField, string>> = {};
  for (const f of CALENDAR_FIELDS) {
    const v = draft.edits[f];
    if (v !== undefined && v !== initial[f]) {
      edits[f] = v;
    }
  }
  return {
    ...draft,
    edits,
    cycle2Added: ctx.cycle2 === null ? draft.cycle2Added : false,
  };
}
